import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

type SortDirection = 'asc' | 'desc' | null;

interface ColumnDef {
  key: string;
  label: string;
  accessor: (row: any) => any;
  align?: 'left' | 'right' | 'center';
  render?: (row: any) => React.ReactNode;
  defaultWidth?: number; // px — initial column width before user drag
}

const COLUMN_WIDTHS_KEY = 'audit_column_widths_v1';
const CHECKBOX_COL_WIDTH = 40;

const formatDuration = (seconds: any): string => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const total = Math.round(n);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (v: number) => String(v).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

const WebsiteAuditPage: React.FC = () => {
  const { projectId, crawlId: urlCrawlId } = useParams<{ projectId: string; crawlId?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [url, setUrl] = useState('');
  const [crawlName, setCrawlName] = useState('');
  const [useSitemap, setUseSitemap] = useState(true);
  const [maxDepth, setMaxDepth] = useState(3);
  const [publishDateXPath, setPublishDateXPath] = useState('');
  const [updatedDateXPath, setUpdatedDateXPath] = useState('');
  // Analytics / Search Console config (written to clients.config.json on crawl start)
  const [gscSiteUrl, setGscSiteUrl] = useState('');
  // Real Search Console properties, so the field is a choice rather than a
  // guess — a mistyped property reads as "no traffic" only after a full crawl.
  const [gscProperties, setGscProperties] = useState<string[]>([]);
  const [ga4PropertyId, setGa4PropertyId] = useState('');

  // Performance options
  const [concurrency, setConcurrency] = useState(10);
  const [requestDelay, setRequestDelay] = useState(0.3);

  // Sitemap picker (pre-crawl discovery)
  type SitemapNode = { url: string; type: string; url_count: number; children?: SitemapNode[]; error?: string; is_likely_blog?: boolean };
  const [sitemapNodes, setSitemapNodes] = useState<SitemapNode[]>([]);
  const [selectedSitemaps, setSelectedSitemaps] = useState<Set<string>>(new Set());
  const [fetchingSitemaps, setFetchingSitemaps] = useState(false);
  const [sitemapMessage, setSitemapMessage] = useState<string>('');
  const [crawlResults, setCrawlResults] = useState<any>(null);
  const [error, setError] = useState('');
  const [projectName, setProjectName] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const [savedCrawls, setSavedCrawls] = useState<any[]>([]);
  const [selectedCrawlId, setSelectedCrawlId] = useState<number | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Table UX state
  const [pageSize, setPageSize] = useState<number>(100); // 50/100/200/All(-1)
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>(null);

  // Send-to-Calendar state
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [showCalModal, setShowCalModal] = useState(false);
  const [calActionType, setCalActionType] = useState<'rewrite' | 'new'>('rewrite');
  const [calScheduledDate, setCalScheduledDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [calNotes, setCalNotes] = useState('');
  const [calSubmitting, setCalSubmitting] = useState(false);
  const [calSuccess, setCalSuccess] = useState('');

  // Fetch-traffic (GA4/GSC/Ahrefs) state
  const [fetchingTraffic, setFetchingTraffic] = useState(false);
  const [trafficMessage, setTrafficMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [trafficWarning, setTrafficWarning] = useState<{ title: string; lines: string[] } | null>(null);

  // Analyze (DELETE / UPGRADE / REDIRECT / KEEP) state
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeMessage, setAnalyzeMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [creatingNotionTask, setCreatingNotionTask] = useState(false);
  const [notionTaskUrl, setNotionTaskUrl] = useState<string | null>(null);

  // Load the account's Search Console properties once, for the picker
  useEffect(() => {
    api
      .get('/website-audit/gsc-properties')
      .then((res) => setGscProperties(res.data?.properties || []))
      .catch(() => { /* non-fatal — field stays free-text */ });
  }, []);

  useEffect(() => {
    if (projectId) {
      loadProjectName();
      loadSavedCrawls();
      // Deep-link: if URL has /:crawlId, auto-load that audit
      if (urlCrawlId && /^\d+$/.test(urlCrawlId)) {
        const cid = parseInt(urlCrawlId, 10);
        if (cid && cid !== selectedCrawlId) {
          loadCrawl(cid);
        }
      }
      // Re-attach to a running crawl if user navigated away and came back.
      // Polls /active-job and, if one is in flight, opens the SSE stream
      // again so live logs continue from where they were.
      (async () => {
        try {
          const res = await api.get(`/website-audit/active-job/${projectId}`);
          if (res.data?.active && res.data.job_id) {
            attachToLogStream(res.data.job_id, /* restoring */ true);
          }
        } catch { /* no active job — ignore */ }
      })();
    }

    // Cleanup SSE connection on unmount
    return () => {
      const eventSource = (window as any).crawlEventSource;
      if (eventSource) {
        eventSource.close();
        delete (window as any).crawlEventSource;
      }
    };
  }, [projectId]);

  // Reusable SSE attach — used by both the new-crawl path and the
  // auto-reconnect-on-mount path. When `restoring=true`, shows a banner
  // log line so the user knows logs are being resumed mid-crawl.
  const attachToLogStream = (jobId: number, restoring: boolean = false) => {
    setCrawling(true);
    if (restoring) {
      addLog(`↻ Reconnecting to live log for running crawl (job #${jobId})…`);
    }
    const token = localStorage.getItem('auth_token') || '';
    const eventSource = new EventSource(
      `${api.defaults.baseURL}/website-audit/logs/${jobId}?token=${token}`
    );

    eventSource.onmessage = (event) => {
      const message = event.data;
      if (message.startsWith(':')) return;
      addLog(message);

      // Only terminate the stream on a TRUE end-of-crawl marker.
      // Inner-crawler [ERROR] lines (e.g. an XML parse failure on a single
      // sitemap) are informational — the crawl continues. Previously we
      // closed on any [ERROR], which dropped the stream after the first
      // bad sitemap and made the UI look stuck.
      const isTerminalSuccess = message.startsWith('[COMPLETE]');
      const isTerminalFailure =
        message.startsWith('[ERROR] Crawl failed:') ||
        message.startsWith('[FATAL]');
      if (isTerminalSuccess || isTerminalFailure) {
        eventSource.close();
        setCrawling(false);
        if (isTerminalSuccess) {
          // Reload the saved-crawls list AND auto-load the just-completed
          // crawl so the summary cards (Pages Crawled, From Sitemap, etc.)
          // populate with the final values instead of staying at 0.
          setTimeout(async () => {
            try {
              const res = await api.get(`/website-audit/crawls/${projectId}`);
              const list = res.data || [];
              setSavedCrawls(list);
              // The most recent matching crawl is the one we just finished.
              const justFinished = list.find((c: any) => c.status === 'completed');
              if (justFinished?.id) {
                loadCrawl(justFinished.id);
              }
            } catch { /* ignore */ }
          }, 1000);
        }
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      eventSource.close();
      setCrawling(false);
      addLog('✗ Log stream connection lost — the crawl may still be running on the server. Click "Show Activity" at the top (next to Home) to track progress, or reload this page to reconnect.');
    };

    (window as any).crawlEventSource = eventSource;
  };

  // Auto-scroll logs to bottom
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Reset page when results change
  useEffect(() => {
    setCurrentPage(1);
  }, [crawlResults, pageSize, sortKey, sortDir]);

  const loadProjectName = async () => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProjectName(response.data.name || 'Project');
    } catch (err) {
      console.error('Failed to load project name:', err);
    }
  };

  const loadSavedCrawls = async () => {
    try {
      const response = await api.get(`/website-audit/crawls/${projectId}`);
      setSavedCrawls(response.data || []);
    } catch (err) {
      console.error('Failed to load saved crawls:', err);
    }
  };

  const handleDeleteCrawl = async (crawlId: number, event: React.MouseEvent) => {
    event.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this crawl?')) {
      return;
    }

    try {
      await api.delete(`/website-audit/crawl/${crawlId}`);
      await loadSavedCrawls();
      if (selectedCrawlId === crawlId) {
        setSelectedCrawlId(null);
        setCrawlResults(null);
      }
    } catch (err: any) {
      setError('Failed to delete crawl: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRenameCrawl = async (crawlId: number, currentName: string, event: React.MouseEvent) => {
    event.stopPropagation();

    const newName = window.prompt('Enter new name for this crawl:', currentName);
    if (!newName || newName === currentName) {
      return;
    }

    try {
      await api.put(`/website-audit/crawl/${crawlId}`, {
        crawl_name: newName
      });
      await loadSavedCrawls();
    } catch (err: any) {
      setError('Failed to rename crawl: ' + (err.response?.data?.detail || err.message));
    }
  };

  const loadCrawl = async (crawlId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/website-audit/crawl/${crawlId}`);
      const crawl = response.data;

      setUrl(crawl.url);
      setCrawlName(crawl.crawl_name);
      setUseSitemap(crawl.use_sitemap);
      setMaxDepth(crawl.max_depth);
      setPublishDateXPath(crawl.publish_date_xpath || '');
      setUpdatedDateXPath(crawl.updated_date_xpath || '');

      setCrawlResults({
        success: true,
        crawl_id: crawl.id,
        pages_crawled: crawl.pages_crawled,
        sitemap_pages: crawl.sitemap_pages,
        crawled_pages: crawl.crawled_pages,
        duration: crawl.duration_seconds,
        pages: crawl.crawl_data,
        message: `Loaded saved crawl: ${crawl.crawl_name}`
      });

      setNotionTaskUrl(crawl.notion_task_url || null);
      setSelectedCrawlId(crawlId);
      setError('');
      // Update browser URL so this audit is shareable.
      if (projectId && String(urlCrawlId) !== String(crawlId)) {
        navigate(`/projects/${projectId}/website-audit/${crawlId}`, { replace: false });
      }
      // reset table view
      setCurrentPage(1);
      setSortKey(null);
      setSortDir(null);
    } catch (err: any) {
      setError('Failed to load crawl: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const verifyGscForUrl = async (pageUrl: string) => {
    if (!selectedCrawlId || !pageUrl) return;
    try {
      const resp = await api.post(
        `/website-audit/crawl/${selectedCrawlId}/verify-gsc-url`,
        { url: pageUrl }
      );
      const { before, after, sources } = resp.data || {};
      // Update local state in-place so the row reflects the new value.
      setCrawlResults((prev: any) => {
        if (!prev?.pages) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p: any) =>
            p.url === pageUrl
              ? {
                  ...p,
                  gsc_clicks: after?.gsc_clicks,
                  gsc_impressions: after?.gsc_impressions,
                  gsc_ctr: after?.gsc_ctr,
                  gsc_position: after?.gsc_position,
                }
              : p
          ),
        };
      });
      const beforeStr = before
        ? `${before.gsc_clicks ?? '-'} clicks / ${before.gsc_impressions ?? '-'} impressions`
        : 'no prior value';
      const afterStr = `${after?.gsc_clicks ?? 0} clicks / ${after?.gsc_impressions ?? 0} impressions`;
      const variantNote = (sources?.length || 0) > 1
        ? ` (summed ${sources.length} URL variants)`
        : '';
      alert(`Verified GSC for:\n${pageUrl}\n\nBefore: ${beforeStr}\nAfter:  ${afterStr}${variantNote}`);
    } catch (err: any) {
      alert('Verify failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Flatten urlset leaves (leaf = no children OR children array empty)
  const collectLeafUrls = (nodes: SitemapNode[]): string[] => {
    const out: string[] = [];
    const walk = (n: SitemapNode) => {
      if (n.children && n.children.length > 0) {
        n.children.forEach(walk);
      } else {
        out.push(n.url);
      }
    };
    nodes.forEach(walk);
    return out;
  };

  const handleFetchSitemaps = async () => {
    if (!url.trim()) {
      setError('Please enter a website URL first');
      return;
    }
    setError('');
    setFetchingSitemaps(true);
    setSitemapMessage('');
    setSitemapNodes([]);
    setSelectedSitemaps(new Set());
    try {
      const response = await api.post('/website-audit/sitemaps/list', { url: url.trim() });
      const nodes: SitemapNode[] = response.data.sitemaps || [];
      setSitemapNodes(nodes);
      const total = response.data.total_urls || 0;
      if (!nodes.length) {
        setSitemapMessage(response.data.message || 'No sitemaps found. Crawl will fall back to link discovery.');
      } else {
        setSitemapMessage(`Found ${nodes.length} sitemap group(s) with ${total} total URLs.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch sitemaps');
    } finally {
      setFetchingSitemaps(false);
    }
  };

  const toggleSitemapSelection = (u: string) => {
    setSelectedSitemaps(prev => {
      const next = new Set(prev);
      if (next.has(u)) next.delete(u); else next.add(u);
      return next;
    });
  };

  const selectAllSitemaps = () => {
    const leaves = collectLeafUrls(sitemapNodes);
    setSelectedSitemaps(new Set(leaves));
  };

  const clearSitemapSelection = () => setSelectedSitemaps(new Set());

  const handleStartCrawl = async () => {
    if (!url.trim()) {
      setError('Please enter a website URL');
      return;
    }

    if (!crawlName.trim()) {
      setError('Please enter a crawl name');
      return;
    }

    setError('');
    setCrawling(true);
    setLogs([]);
    setCrawlResults(null);
    setNotionTaskUrl(null);

    addLog('Starting website crawl with enhanced headers...');
    addLog(`Crawl Name: ${crawlName.trim()}`);
    addLog(`Target URL: ${url.trim()}`);
    addLog(`Use Sitemap: ${useSitemap ? 'Yes' : 'No'}`);
    addLog(`Max Depth: ${maxDepth}`);
    addLog(`Concurrency: ${concurrency} parallel workers`);
    addLog(`Request Delay: ${requestDelay}s`);

    try {
      const selectedSitemapList = Array.from(selectedSitemaps);
      if (selectedSitemapList.length > 0) {
        addLog(`Tagged ${selectedSitemapList.length} sitemap(s) as BLOG — pages outside these will not be marked DELETE`);
      } else {
        addLog('No blog sitemaps tagged — DELETE eligibility falls back to URL-pattern detection (/blog, /news, etc.)');
      }
      const response = await api.post(`/website-audit/crawl`, {
        project_id: parseInt(projectId || '0'),
        url: url.trim(),
        crawl_name: crawlName.trim(),
        use_sitemap: useSitemap,
        max_depth: maxDepth,
        publish_date_xpath: publishDateXPath.trim() || null,
        updated_date_xpath: updatedDateXPath.trim() || null,
        concurrency: concurrency,
        request_delay: requestDelay,
        gsc_site_url: gscSiteUrl.trim() || null,
        ga4_property_id: ga4PropertyId.trim() || null,
        // Tag list (semantic), NOT a crawl scope — full site is always crawled.
        blog_sitemap_urls: selectedSitemapList.length > 0 ? selectedSitemapList : null,
      });

      const jobId = response.data.job_id;
      addLog(`Job ID: ${jobId} - Connecting to real-time log stream...`);
      attachToLogStream(jobId);

    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to start crawl';
      addLog(`✗ Error: ${errorMsg}`);
      setError(errorMsg);
      setCrawling(false);
    }
  };

  // -------- Table helpers --------

  const columns: ColumnDef[] = useMemo(() => [
    { key: 'url', label: 'URL', accessor: (r) => r.url || '', defaultWidth: 320 },
    { key: 'decision', label: 'Decision', accessor: (r) => r.decision || '', align: 'center', defaultWidth: 110 },
    { key: 'days_since_published', label: 'Days Since Pub', accessor: (r) => r.days_since_published ?? -1, align: 'center', defaultWidth: 110 },
    { key: 'days_since_updated', label: 'Days Since Upd', accessor: (r) => r.days_since_updated ?? -1, align: 'center', defaultWidth: 110 },
    { key: 'gsc_clicks', label: 'Clicks', accessor: (r) => r.gsc_clicks ?? -1, align: 'right', defaultWidth: 90 },
    { key: 'gsc_impressions', label: 'Impressions', accessor: (r) => r.gsc_impressions ?? -1, align: 'right', defaultWidth: 110 },
    { key: 'ahrefs_backlinks', label: 'Backlinks', accessor: (r) => r.ahrefs_backlinks ?? -1, align: 'right', defaultWidth: 110 },
    { key: 'bing_referrals', label: 'Bing', accessor: (r) => r.bing_referrals ?? -1, align: 'right', defaultWidth: 80 },
    { key: 'chatgpt_referrals', label: 'ChatGPT', accessor: (r) => r.chatgpt_referrals ?? -1, align: 'right', defaultWidth: 90 },
    { key: 'claude_referrals', label: 'Claude', accessor: (r) => r.claude_referrals ?? -1, align: 'right', defaultWidth: 90 },
    { key: 'perplexity_referrals', label: 'Perplexity', accessor: (r) => r.perplexity_referrals ?? -1, align: 'right', defaultWidth: 100 },
    { key: 'yahoo_referrals', label: 'Yahoo', accessor: (r) => r.yahoo_referrals ?? -1, align: 'right', defaultWidth: 80 },
    { key: 'duckduckgo_referrals', label: 'DuckDuckGo', accessor: (r) => r.duckduckgo_referrals ?? -1, align: 'right', defaultWidth: 110 },
    { key: 'ga4_avg_engagement', label: 'GA4 Avg. Session Duration', accessor: (r) => r.ga4_avg_engagement ?? -1, align: 'right', defaultWidth: 140 },
    { key: 'word_count', label: 'Word Count', accessor: (r) => r.word_count ?? 0, align: 'right', defaultWidth: 100 },
    { key: 'title', label: 'Title', accessor: (r) => r.title || '', defaultWidth: 240 },
    { key: 'meta_description', label: 'Description', accessor: (r) => r.meta_description || '', defaultWidth: 320 },
  ], []);

  // Resizable columns — widths persist in localStorage so per-column drag
  // sticks across sessions. Falls back to defaultWidth on the column def.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(COLUMN_WIDTHS_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return {};
  });

  useEffect(() => {
    try { localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [columnWidths]);

  const colWidth = (key: string, fallback: number): number =>
    Math.max(40, columnWidths[key] || fallback);

  const startColumnResize = (key: string, currentWidth: number, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(60, Math.min(1000, currentWidth + (ev.clientX - startX)));
      setColumnWidths(prev => ({ ...prev, [key]: next }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const resetColumnWidths = () => setColumnWidths({});

  const sortedPages = useMemo(() => {
    const pages: any[] = crawlResults?.pages || [];
    if (!sortKey || !sortDir) return pages;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return pages;

    const sorted = [...pages].sort((a, b) => {
      const va = col.accessor(a);
      const vb = col.accessor(b);

      // Numeric comparison
      if (typeof va === 'number' && typeof vb === 'number') {
        return va - vb;
      }
      const sa = String(va ?? '').toLowerCase();
      const sb = String(vb ?? '').toLowerCase();
      return sa.localeCompare(sb);
    });
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [crawlResults, sortKey, sortDir, columns]);

  const totalRows = sortedPages.length;
  const effectivePageSize = pageSize === -1 ? totalRows : pageSize;
  const totalPages = effectivePageSize > 0 ? Math.max(1, Math.ceil(totalRows / effectivePageSize)) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const pageSlice = useMemo(() => {
    if (pageSize === -1) return sortedPages;
    const start = (safePage - 1) * effectivePageSize;
    return sortedPages.slice(start, start + effectivePageSize);
  }, [sortedPages, safePage, effectivePageSize, pageSize]);

  const handleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
      return;
    }
    // cycle: asc -> desc -> off
    if (sortDir === 'asc') setSortDir('desc');
    else if (sortDir === 'desc') { setSortKey(null); setSortDir(null); }
    else setSortDir('asc');
  };

  const sortIndicator = (key: string) => {
    if (sortKey !== key) return <span className="text-gray-300 ml-1">⇅</span>;
    if (sortDir === 'asc') return <span className="text-blue-600 ml-1">▲</span>;
    if (sortDir === 'desc') return <span className="text-blue-600 ml-1">▼</span>;
    return <span className="text-gray-300 ml-1">⇅</span>;
  };

  const escapeCsv = (val: any): string => {
    if (val === null || val === undefined) return '';
    const s = String(val);
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const toggleUrlSelect = (url: string) => {
    const s = new Set(selectedUrls);
    if (s.has(url)) s.delete(url); else s.add(url);
    setSelectedUrls(s);
  };

  const toggleSelectAllOnPage = () => {
    const s = new Set(selectedUrls);
    const pageUrls = pageSlice.map((p: any) => p.url).filter(Boolean);
    const allOnPageSelected = pageUrls.every(u => s.has(u));
    if (allOnPageSelected) {
      pageUrls.forEach(u => s.delete(u));
    } else {
      pageUrls.forEach(u => s.add(u));
    }
    setSelectedUrls(s);
  };

  const handleSendToCalendar = async () => {
    if (selectedUrls.size === 0) return;
    setCalSubmitting(true);
    setCalSuccess('');
    try {
      const allPages: any[] = crawlResults?.pages || [];
      const selectedPages = allPages.filter(p => p.url && selectedUrls.has(p.url));
      const entries = selectedPages.map(p => ({
        url: p.url,
        title: p.title || '',
        keyword: p.title || p.url,
        scheduled_date: calScheduledDate,
        notes: calNotes,
        action_type: calActionType,
        category: 'audit',
        priority: 'medium',
      }));
      const res = await api.post('/content-calendar/entries/from-audit', {
        project_id: parseInt(projectId || '0'),
        entries,
      });
      setCalSuccess(`Added ${res.data.created} entries to Content Calendar`);
      setSelectedUrls(new Set());
      setTimeout(() => {
        setShowCalModal(false);
        setCalSuccess('');
      }, 1500);
    } catch (err: any) {
      setError('Failed to send to calendar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCalSubmitting(false);
    }
  };

  const handleFetchTraffic = async () => {
    if (!selectedCrawlId) {
      setTrafficMessage({ kind: 'err', text: 'Select a saved crawl first.' });
      return;
    }
    setFetchingTraffic(true);
    setTrafficMessage({ kind: 'ok', text: 'Starting traffic enrichment…' });

    try {
      // Kick off background task (returns 202 immediately)
      const kick = await api.post(`/website-audit/crawl/${selectedCrawlId}/fetch-traffic`, {
        days: 365,
        url_inspection_enabled: true,
        url_inspection_cap: 500,
      });
      const clientName = kick.data?.client_name || '';
      const totalPages = kick.data?.total_pages ?? 0;

      // Poll status. URL Inspection is one GSC API call per URL, so the run scales
      // with the crawl size — budget ~3s/page on top of a 10 min floor rather than
      // giving up on every large site at a flat 20 minutes.
      const pollStart = Date.now();
      const FAST_POLL_MS = 3000;
      const SLOW_POLL_MS = 30000;
      const SLOW_AFTER_MS = 5 * 60 * 1000; // chatty polling for the first 5 min only
      const POLL_TIMEOUT_MS = Math.min(
        Math.max(10 * 60 * 1000 + (totalPages || 0) * 3000, 20 * 60 * 1000),
        3 * 60 * 60 * 1000,
      );
      let lastPhase = '';

      while (true) {
        const elapsed = Date.now() - pollStart;
        await new Promise((r) => setTimeout(r, elapsed > SLOW_AFTER_MS ? SLOW_POLL_MS : FAST_POLL_MS));
        if (Date.now() - pollStart > POLL_TIMEOUT_MS) {
          const mins = Math.round(POLL_TIMEOUT_MS / 60000);
          setTrafficMessage({
            kind: 'err',
            text: `Still running after ${mins} min — the job continues on the server and keeps saving. `
              + 'Reload this page in a few minutes to pick up the finished data. Do not run Analyze until it completes.',
          });
          break;
        }
        let info: any = null;
        try {
          const s = await api.get(`/website-audit/crawl/${selectedCrawlId}/fetch-traffic/status`);
          info = s.data || {};
        } catch {
          continue; // transient error, keep polling
        }

        // "idle" means the backend restarted and lost the in-flight job
        if (info.status === 'idle') {
          setTrafficMessage({ kind: 'err', text: 'Enrichment lost (backend restarted). Please click Fetch Traffic Data again.' });
          break;
        }

        const phase = info.phase || info.status || '';
        const matched = info.matched || {};
        const ahrefsChecked = info.ahrefs_checked ?? null;
        const inspectionChecked = info.inspection_checked ?? null;
        const totalPages = info.total_pages ?? 0;
        const phaseLabel: Record<string, string> = {
          starting: 'Starting…',
          ahrefs: `Fetching Ahrefs backlinks…${ahrefsChecked !== null ? ` (${ahrefsChecked}/${totalPages} checked)` : ''}`,
          ahrefs_done: 'Ahrefs done',
          ga4_gsc: 'Fetching GA4 + GSC…',
          ga4_gsc_done: 'GA4 + GSC fetched',
          url_inspection: `Running URL Inspection…${inspectionChecked !== null ? ` (${inspectionChecked}/${Math.min(totalPages, 500)} checked)` : ''}`,
          inspection_done: 'URL Inspection complete',
          done: 'Done',
        };

        // Reload the table after any phase change so partial progress is visible
        if (phase && phase !== lastPhase) {
          lastPhase = phase;
          try {
            const fresh = await api.get(`/website-audit/crawl/${selectedCrawlId}`);
            // Preserve summary fields (pages_crawled, sitemap_pages, duration)
            // — only overwrite the pages array with the latest enrichment data.
            setCrawlResults((prev: any) => ({
              ...(prev || {}),
              success: true,
              crawl_id: fresh.data.id,
              pages_crawled: fresh.data.pages_crawled,
              sitemap_pages: fresh.data.sitemap_pages,
              crawled_pages: fresh.data.crawled_pages,
              duration: fresh.data.duration_seconds,
              pages: fresh.data.crawl_data || [],
            }));
          } catch { /* non-fatal */ }
        }

        setTrafficMessage({
          kind: info.status === 'failed' ? 'err' : 'ok',
          text:
            info.status === 'failed'
              ? `Enrichment failed: ${info.error || 'unknown error'}`
              : `${clientName ? clientName + ' — ' : ''}${phaseLabel[phase] || phase || 'Working…'} | ` +
                `GA4: ${matched.ga4 ?? 0}, GSC: ${matched.gsc ?? 0}, AI: ${matched.ai ?? 0}, ` +
                `Ahrefs: ${matched.ahrefs ?? 0}, Inspection: ${matched.inspection ?? 0} ` +
                `(of ${totalPages} pages)`,
        });

        if (info.status === 'completed' || info.status === 'failed') {
          // Final reload to make sure we have all data
          try {
            const fresh = await api.get(`/website-audit/crawl/${selectedCrawlId}`);
            setCrawlResults((prev: any) => ({
              ...(prev || {}),
              success: true,
              crawl_id: fresh.data.id,
              pages_crawled: fresh.data.pages_crawled,
              sitemap_pages: fresh.data.sitemap_pages,
              crawled_pages: fresh.data.crawled_pages,
              duration: fresh.data.duration_seconds,
              pages: fresh.data.crawl_data || [],
            }));
          } catch { /* non-fatal */ }

          // Warn if no data sources returned anything
          const m = info.matched || {};
          // Sources that couldn't be queried at all (bad property ID / no
          // permission) — a real error, not an empty result.
          const srcErrors: Record<string, string> = info.source_errors || {};
          const noGsc = !m.gsc;
          const noGa4 = !m.ga4;
          const noAhrefs = !m.ahrefs;
          if (noGsc || noGa4 || noAhrefs) {
            const missing: string[] = [];
            if (noGsc) missing.push('Google Search Console (GSC) — no clicks/impressions/position data');
            if (noGa4) missing.push('Google Analytics 4 (GA4) — no sessions/users data');
            if (noAhrefs) missing.push('Ahrefs — no backlinks/DR data');

            const errorLines: string[] = [];
            if (srcErrors.gsc) errorLines.push(`• GSC: ${srcErrors.gsc}`);
            if (srcErrors.ga4) errorLines.push(`• GA4: ${srcErrors.ga4}`);

            setTrafficWarning({
              title: errorLines.length
                ? 'A data source could not be queried'
                : 'Some data sources returned no data',
              lines: errorLines.length
                ? [
                    'These sources returned an error rather than empty results:',
                    ...errorLines,
                    '',
                    'This is a configuration problem, not missing traffic. Check',
                    'gsc_site_url / ga4_property_id in clients.config.json — the',
                    'GSC property must be written exactly as it appears in Search',
                    'Console (e.g. "sc-domain:example.com"), not as a bare domain.',
                  ]
                : [
                    'The following sources returned 0 matches for this domain:',
                    ...missing,
                    '',
                    'Possible reasons:',
                    '• This domain is not verified in our GSC service account',
                    '• The GA4 property ID is not set in clients.config.json',
                    '• The domain has no data in the selected date range',
                    '• Contact your admin to connect this domain to GSC / GA4',
                  ],
            });
          }
          break;
        }
      }
    } catch (err: any) {
      const detail: string =
        err.response?.data?.detail ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        err.message || 'Unknown error';

      // Domain not configured → show explanatory popup instead of just inline text
      if (err.response?.status === 400 && detail.toLowerCase().includes('no seo dashboard client matched')) {
        setTrafficWarning({
          title: 'Domain not connected to data sources',
          lines: [
            `This domain is not configured in our SEO data sources.`,
            '',
            'To fetch traffic data you need to:',
            '1. Add this domain to seo-dashboard/clients.config.json',
            '2. Set gsc_site_url (e.g. sc-domain:yourdomain.com)',
            '3. Set ga4_property_id (e.g. properties/123456789)',
            '4. Restart the backend to reload the config',
            '',
            'Until then, GSC, GA4, and Ahrefs columns will remain empty.',
          ],
        });
        setTrafficMessage({ kind: 'err', text: 'Domain not connected — see popup for details.' });
      } else {
        setTrafficMessage({
          kind: 'err',
          text: 'Failed: ' + detail,
        });
      }
    } finally {
      setFetchingTraffic(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedCrawlId) return;
    setAnalyzing(true);
    setAnalyzeMessage({ kind: 'ok', text: 'Analyzing pages…' });
    try {
      // Enrichment may still be running server-side even when this tab stopped
      // watching it (poll timeout, reload, different browser). Analysing now would
      // classify pages on incomplete indexation/backlink data, so block instead.
      try {
        const st = await api.get(`/website-audit/crawl/${selectedCrawlId}/fetch-traffic/status`);
        if (st.data?.status === 'running') {
          const phase = st.data?.phase || 'running';
          setAnalyzeMessage({
            kind: 'err',
            text: `Traffic enrichment is still running (${phase}). Wait for it to finish — `
              + 'analysing now would classify pages on incomplete data.',
          });
          return;
        }
      } catch { /* status unavailable — fall through and analyse */ }

      const resp = await api.post(`/website-audit/crawl/${selectedCrawlId}/analyze`);
      const d = resp.data || {};
      const dec = d.decisions || {};
      // Reload the crawl so the Decision column shows up in the table.
      const fresh = await api.get(`/website-audit/crawl/${selectedCrawlId}`);
      setCrawlResults((prev: any) => ({
        ...(prev || {}),
        success: true,
        crawl_id: fresh.data.id,
        pages_crawled: fresh.data.pages_crawled,
        sitemap_pages: fresh.data.sitemap_pages,
        crawled_pages: fresh.data.crawled_pages,
        duration: fresh.data.duration_seconds,
        pages: fresh.data.crawl_data || [],
      }));
      setAnalyzeMessage({
        kind: 'ok',
        text: `Analyzed ${d.total ?? 0} pages — DELETE ${dec.DELETE ?? 0} · UPGRADE ${dec.UPGRADE ?? 0} · REDIRECT ${dec.REDIRECT ?? 0} · KEEP ${dec.KEEP ?? 0}`,
      });
    } catch (err: any) {
      setAnalyzeMessage({
        kind: 'err',
        text: 'Failed: ' + (err.response?.data?.detail || err.message),
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownloadCsv = () => {
    if (!sortedPages || sortedPages.length === 0) return;
    const header = columns.map(c => escapeCsv(c.label)).join(',');
    const rows = sortedPages.map((row: any) => {
      return [
        row.url,
        row.decision ?? '',
        row.days_since_published ?? '',
        row.days_since_updated ?? '',
        row.gsc_clicks ?? '',
        row.gsc_impressions ?? '',
        row.ahrefs_backlinks ?? '',
        row.bing_referrals ?? '',
        row.chatgpt_referrals ?? '',
        row.claude_referrals ?? '',
        row.perplexity_referrals ?? '',
        row.yahoo_referrals ?? '',
        row.duckduckgo_referrals ?? '',
        formatDuration(row.ga4_avg_engagement),
        row.word_count ?? '',
        row.title ?? '',
        row.meta_description ?? '',
      ].map(escapeCsv).join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = (crawlName || `crawl-${selectedCrawlId || 'results'}`).replace(/[^a-z0-9-_]+/gi, '_');
    a.href = dlUrl;
    a.download = `${safeName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(dlUrl);
  };

  return (
    <Layout projectId={parseInt(projectId || '0')} projectName={projectName}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Website Audit</h1>
          <p className="text-gray-600">
            Crawl and analyze your website with automatic sitemap discovery
          </p>
        </div>

        {/* Crawl Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Start New Crawl</h2>

          {/* Crawl Name Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Crawl Name *
            </label>
            <input
              type="text"
              value={crawlName}
              onChange={(e) => setCrawlName(e.target.value)}
              placeholder="e.g., Initial Audit - Jan 2024"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={crawling}
            />
            <p className="text-xs text-gray-500 mt-1">
              Give this crawl a unique name to identify it later
            </p>
          </div>

          {/* URL Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website URL *
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={crawling}
              />
              <button
                type="button"
                onClick={handleFetchSitemaps}
                disabled={crawling || fetchingSitemaps || !url.trim()}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                title="Discover sitemaps for this URL"
              >
                {fetchingSitemaps ? 'Fetching…' : 'Fetch Sitemaps'}
              </button>
            </div>
          </div>

          {/* Analytics & Search Console */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GSC Site URL
              </label>
              <input
                type="text"
                list="gsc-properties"
                value={gscSiteUrl}
                onChange={(e) => setGscSiteUrl(e.target.value)}
                placeholder="Pick a verified property…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                disabled={crawling}
              />
              <datalist id="gsc-properties">
                {gscProperties.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              {gscSiteUrl && gscProperties.length > 0 && !gscProperties.includes(gscSiteUrl.trim()) && (
                <p className="text-xs text-amber-600 mt-1">
                  Not one of the {gscProperties.length} verified properties — we'll try to
                  match it by domain, but pick from the list to be sure.
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Google Search Console property — leave blank to auto-detect from the domain
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GA4 Property ID
              </label>
              <input
                type="text"
                value={ga4PropertyId}
                onChange={(e) => setGa4PropertyId(e.target.value)}
                placeholder="348541832"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                disabled={crawling}
              />
              <p className="text-xs text-gray-500 mt-1">
                Numeric ID from GA4 (e.g. 348541832)
              </p>
            </div>
          </div>

          {/* Sitemap picker — tags blog sitemaps for DELETE eligibility (does NOT scope crawl) */}
          {(sitemapMessage || sitemapNodes.length > 0) && (
            <div className="mb-4 border border-gray-200 rounded-lg p-3 bg-gray-50">
              {sitemapNodes.length > 0 && (
                <div className="mb-2">
                  <p className="text-sm font-semibold text-gray-800">Mark which sitemaps are BLOG sitemaps</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Pages in tagged sitemaps become eligible for <span className="font-medium text-red-600">DELETE</span> suggestions.
                    Pages outside tagged sitemaps get only UPGRADE / REDIRECT / KEEP. The full site is always crawled.
                  </p>
                </div>
              )}
              {sitemapMessage && (
                <p className="text-xs text-gray-700 mb-2">{sitemapMessage}</p>
              )}
              {sitemapNodes.length > 0 && (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      type="button"
                      onClick={selectAllSitemaps}
                      disabled={crawling}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={clearSitemapSelection}
                      disabled={crawling}
                      className="text-xs px-2 py-1 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50"
                    >
                      Clear
                    </button>
                    <span className="text-xs text-gray-600 ml-auto">
                      {selectedSitemaps.size === 0
                        ? 'No blog sitemaps tagged — DELETE will use URL-pattern fallback'
                        : `${selectedSitemaps.size} sitemap(s) tagged as blog`}
                    </span>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 text-sm">
                    {sitemapNodes.map((node, i) => (
                      <SitemapNodeRow
                        key={`${node.url}-${i}`}
                        node={node}
                        depth={0}
                        selected={selectedSitemaps}
                        onToggle={toggleSitemapSelection}
                        disabled={crawling}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={useSitemap}
                  onChange={(e) => setUseSitemap(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={crawling}
                />
                <span className="text-sm font-medium text-gray-700">
                  Use Sitemap (Recommended)
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Auto-discover sitemap from robots.txt and crawl ALL pages
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Crawl Depth (Fallback Only)
              </label>
              <input
                type="number"
                value={maxDepth}
                onChange={(e) => setMaxDepth(parseInt(e.target.value) || 3)}
                min="1"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={crawling}
              />
              <p className="text-xs text-gray-500 mt-1">
                Used only if sitemap not found
              </p>
            </div>
          </div>

          {/* XPath Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Publish Date XPath (Optional)
              </label>
              <input
                type="text"
                value={publishDateXPath}
                onChange={(e) => setPublishDateXPath(e.target.value)}
                placeholder="//meta[@property='article:published_time']/@content"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                disabled={crawling}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to use default fallbacks
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Updated Date XPath (Optional)
              </label>
              <input
                type="text"
                value={updatedDateXPath}
                onChange={(e) => setUpdatedDateXPath(e.target.value)}
                placeholder="//meta[@property='article:modified_time']/@content"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                disabled={crawling}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to use default fallbacks
              </p>
            </div>
          </div>

          {/* Performance Options */}
          <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">⚡ Performance Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Concurrent Workers
                </label>
                <input
                  type="number"
                  value={concurrency}
                  onChange={(e) => setConcurrency(parseInt(e.target.value) || 10)}
                  min="1"
                  max="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={crawling}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Parallel page fetches (1-50). Higher = faster.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Request Delay (sec)
                </label>
                <input
                  type="number"
                  value={requestDelay}
                  onChange={(e) => setRequestDelay(parseFloat(e.target.value) || 0.3)}
                  min="0"
                  max="5"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={crawling}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Delay between requests. Lower = faster.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Start Button */}
          <button
            onClick={handleStartCrawl}
            disabled={crawling || !url.trim() || !crawlName.trim()}
            className={`w-full px-6 py-3 rounded-lg font-medium transition-colors ${
              crawling || !url.trim() || !crawlName.trim()
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {crawling ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin h-5 w-5 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Crawling...
              </span>
            ) : (
              'Start Crawl'
            )}
          </button>
        </div>

        {/* Saved Crawls (moved below Start Crawl) */}
        {savedCrawls.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Saved Crawls</h2>
            <div className="space-y-2">
              {savedCrawls.map((crawl) => (
                <div
                  key={crawl.id}
                  onClick={() => loadCrawl(crawl.id)}
                  className={`p-4 border rounded-lg cursor-pointer hover:bg-blue-50 transition-colors ${
                    selectedCrawlId === crawl.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{crawl.crawl_name}</h3>
                      <p className="text-sm text-gray-500">{crawl.url}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{crawl.pages_crawled} pages</p>
                        <p className="text-xs text-gray-500">
                          {new Date(crawl.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleRenameCrawl(crawl.id, crawl.crawl_name, e)}
                          className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
                          title="Rename crawl"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDeleteCrawl(crawl.id, e)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-100 rounded-md transition-colors"
                          title="Delete crawl"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  {crawl.status === 'failed' && (
                    <p className="text-xs text-red-600 mt-2">Failed: {crawl.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terminal/Console Logger */}
        {logs.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-semibold text-gray-900">Crawl Log</h2>
              <button
                onClick={() => setLogs([])}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear Log
              </button>
            </div>
            <div
              ref={logContainerRef}
              className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-green-400 h-64 overflow-y-auto"
            >
              {logs.map((log, idx) => (
                <div key={idx} className="mb-1">
                  {log}
                </div>
              ))}
              {crawling && (
                <div className="flex items-center mt-2">
                  <div className="animate-pulse">▊</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading indicator when loading saved crawl */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 text-center">
            <div className="inline-flex items-center text-gray-600">
              <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading crawl results...
            </div>
          </div>
        )}

        {/* Crawl Results */}
        {crawlResults && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Crawl Results</h2>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Pages Crawled</p>
                <p className="text-3xl font-bold text-blue-900">
                  {crawlResults.pages_crawled || 0}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">From Sitemap</p>
                <p className="text-3xl font-bold text-green-900">
                  {crawlResults.sitemap_pages || 0}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">Crawled</p>
                <p className="text-3xl font-bold text-purple-900">
                  {crawlResults.crawled_pages || 0}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 font-medium">Duration</p>
                <p className="text-3xl font-bold text-gray-900">
                  {crawlResults.duration || '0'}s
                </p>
              </div>
            </div>

            {/* Pages Table */}
            {crawlResults.pages && crawlResults.pages.length > 0 && (
              <div>
                {/* Table Toolbar */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Discovered Pages ({totalRows.toLocaleString()})
                  </h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm text-gray-600 flex items-center gap-2">
                      Rows per page:
                      <select
                        value={pageSize}
                        onChange={(e) => setPageSize(parseInt(e.target.value))}
                        className="px-2 py-1 border border-gray-300 rounded-md text-sm"
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={-1}>All</option>
                      </select>
                    </label>
                    <button
                      onClick={() => { if (selectedUrls.size > 0) setShowCalModal(true); }}
                      disabled={selectedUrls.size === 0}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
                        selectedUrls.size === 0
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'text-white bg-indigo-600 hover:bg-indigo-700'
                      }`}
                      title="Send selected rows to Content Calendar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Send to Calendar{selectedUrls.size > 0 ? ` (${selectedUrls.size})` : ''}
                    </button>
                    <button
                      onClick={handleFetchTraffic}
                      disabled={fetchingTraffic || !selectedCrawlId}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
                        fetchingTraffic || !selectedCrawlId
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'text-white bg-purple-600 hover:bg-purple-700'
                      }`}
                      title="Fetch GA4 / GSC / Ahrefs / URL Inspection data for this crawl"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {fetchingTraffic ? 'Fetching…' : 'Fetch Traffic Data'}
                    </button>
                    <button
                      onClick={handleAnalyze}
                      disabled={analyzing || fetchingTraffic || !selectedCrawlId}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${
                        analyzing || fetchingTraffic || !selectedCrawlId
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'text-white bg-orange-600 hover:bg-orange-700'
                      }`}
                      title={
                        fetchingTraffic
                          ? 'Wait for traffic enrichment to finish before analysing'
                          : 'Classify each URL as DELETE / UPGRADE / REDIRECT / KEEP'
                      }
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      {analyzing ? 'Analyzing…' : 'Analyze'}
                    </button>
                    <button
                      onClick={handleDownloadCsv}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors inline-flex items-center gap-1"
                      title="Download CSV"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                      </svg>
                      Download CSV
                    </button>
                  </div>
                </div>

                {/* Traffic fetch status message */}
                {trafficMessage && (
                  <div
                    className={`mb-3 p-3 rounded-md text-sm ${
                      trafficMessage.kind === 'ok'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}
                  >
                    {trafficMessage.text}
                  </div>
                )}

                {/* Analyze status message */}
                {analyzeMessage && (
                  <div
                    className={`mb-3 p-3 rounded-md text-sm ${
                      analyzeMessage.kind === 'ok'
                        ? 'bg-orange-50 border border-orange-200 text-orange-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}
                  >
                    {analyzeMessage.text}
                  </div>
                )}

                <div className="flex items-center justify-end mb-1">
                  <button
                    type="button"
                    onClick={resetColumnWidths}
                    className="text-[11px] text-gray-500 hover:text-gray-700 underline"
                    title="Reset all column widths to defaults"
                  >
                    Reset column widths
                  </button>
                </div>
                <div className="overflow-auto border border-gray-200 rounded-lg max-h-[75vh]">
                  {(() => {
                    const totalTableWidth =
                      CHECKBOX_COL_WIDTH +
                      columns.reduce((sum, c) => sum + colWidth(c.key, c.defaultWidth || 140), 0);
                    return (
                  <table
                    className="divide-y divide-gray-200 text-xs"
                    style={{ tableLayout: 'fixed', width: totalTableWidth }}
                  >
                    <colgroup>
                      <col style={{ width: CHECKBOX_COL_WIDTH }} />
                      {columns.map(col => (
                        <col key={col.key} style={{ width: colWidth(col.key, col.defaultWidth || 140) }} />
                      ))}
                    </colgroup>
                    <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                      <tr>
                        <th className="px-3 py-2 text-left bg-gray-50">
                          <input
                            type="checkbox"
                            checked={pageSlice.length > 0 && pageSlice.every((p: any) => p.url && selectedUrls.has(p.url))}
                            onChange={toggleSelectAllOnPage}
                            title="Select all on this page"
                          />
                        </th>
                        {columns.map(col => {
                          const width = colWidth(col.key, col.defaultWidth || 140);
                          return (
                            <th
                              key={col.key}
                              onClick={() => handleSort(col.key)}
                              className={`relative px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100 bg-gray-50 ${
                                col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                              }`}
                            >
                              <span className="inline-flex items-center truncate">
                                {col.label}
                                {sortIndicator(col.key)}
                              </span>
                              {/* Resize handle */}
                              <span
                                onMouseDown={(e) => startColumnResize(col.key, width, e)}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 active:bg-blue-500"
                                title="Drag to resize column"
                              />
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pageSlice.map((page: any, idx: number) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedUrls.has(page.url)}
                              onChange={() => toggleUrlSelect(page.url)}
                            />
                          </td>
                          {/* URL */}
                          <td className="px-3 py-2 text-sm">
                            <div className="flex items-center gap-1.5">
                              <a
                                href={page.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                              >
                                {page.url}
                              </a>
                              {page.from_blog_sitemap === true && (
                                <span
                                  className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700"
                                  title="Tagged as blog content via sitemap selection — eligible for DELETE"
                                >
                                  BLOG
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Decision */}
                          <td className="px-3 py-2 text-sm text-center">
                            {page.decision ? (
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${
                                  page.decision === 'DELETE'
                                    ? 'bg-red-100 text-red-700'
                                    : page.decision === 'UPGRADE'
                                    ? 'bg-amber-100 text-amber-800'
                                    : page.decision === 'REDIRECT'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-green-100 text-green-700'
                                }`}
                                title={page.decision_reason || ''}
                              >
                                {page.decision}
                              </span>
                            ) : '-'}
                          </td>
                          {/* Days Since Published */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-center">
                            {page.days_since_published !== null && page.days_since_published !== undefined ? page.days_since_published : '-'}
                          </td>
                          {/* Days Since Updated */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-center">
                            {page.days_since_updated !== null && page.days_since_updated !== undefined ? page.days_since_updated : '-'}
                          </td>
                          {/* Clicks */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            <span className="inline-flex items-center justify-end gap-1">
                              {page.gsc_clicks !== null && page.gsc_clicks !== undefined ? page.gsc_clicks.toLocaleString() : '-'}
                              <button
                                onClick={(e) => { e.stopPropagation(); verifyGscForUrl(page.url); }}
                                className="text-[10px] text-gray-400 hover:text-blue-600"
                                title="Re-fetch GSC data for this URL (variants summed)"
                              >↻</button>
                            </span>
                          </td>
                          {/* Impressions */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.gsc_impressions !== null && page.gsc_impressions !== undefined ? page.gsc_impressions.toLocaleString() : '-'}
                          </td>
                          {/* Backlinks */}
                          <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">
                            {page.ahrefs_backlinks !== null && page.ahrefs_backlinks !== undefined ? page.ahrefs_backlinks.toLocaleString() : '-'}
                          </td>
                          {/* Bing */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.bing_referrals ?? '-'}
                          </td>
                          {/* ChatGPT */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.chatgpt_referrals ?? '-'}
                          </td>
                          {/* Claude */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.claude_referrals ?? '-'}
                          </td>
                          {/* Perplexity */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.perplexity_referrals ?? '-'}
                          </td>
                          {/* Yahoo */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.yahoo_referrals ?? '-'}
                          </td>
                          {/* DuckDuckGo */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.duckduckgo_referrals ?? '-'}
                          </td>
                          {/* GA4 Avg. Session Duration */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right tabular-nums">
                            {formatDuration(page.ga4_avg_engagement)}
                          </td>
                          {/* Word Count */}
                          <td className="px-3 py-2 text-sm text-gray-600 text-right">
                            {page.word_count?.toLocaleString() || '-'}
                          </td>
                          {/* Title */}
                          <td className="px-3 py-2 text-sm text-gray-700 truncate" title={page.title}>
                            {page.title || '-'}
                          </td>
                          {/* Description */}
                          <td className="px-3 py-2 text-sm text-gray-600 truncate" title={page.meta_description}>
                            {page.meta_description || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                    );
                  })()}
                </div>

                {/* Share footer — visible whenever a saved crawl is loaded */}
                {selectedCrawlId && crawlResults?.pages?.length > 0 && (
                  <div className="mt-4 mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg flex flex-wrap items-center gap-3">
                    <span className="text-xs text-gray-500">Share this audit:</span>
                    <button
                      onClick={() => {
                        const link = `${window.location.origin}/projects/${projectId}/website-audit/${selectedCrawlId}`;
                        navigator.clipboard.writeText(link);
                        alert('Share link copied to clipboard:\n' + link);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md"
                      title="Copy a direct link to this audit"
                    >
                      📋 Copy Share Link
                    </button>
                    <button
                      onClick={() => {
                        const pages = (crawlResults?.pages || []) as any[];
                        const counts: Record<string, number> = {};
                        for (const p of pages) {
                          const d = p.decision || 'UNCLASSIFIED';
                          counts[d] = (counts[d] || 0) + 1;
                        }
                        const link = `${window.location.origin}/projects/${projectId}/website-audit/${selectedCrawlId}`;
                        const top = (decision: string) => pages
                          .filter((p: any) => p.decision === decision)
                          .slice(0, 25)
                          .map((p: any) => `- [${p.url}](${p.url}) — ${p.decision_reason || ''}`)
                          .join('\n');
                        const md = [
                          `# Website Audit — ${url || ''}`,
                          ``,
                          `**Audit link:** ${link}`,
                          ``,
                          `**Total pages:** ${pages.length}`,
                          ...(counts.DELETE ? [`**DELETE:** ${counts.DELETE}`] : []),
                          ...(counts.UPGRADE ? [`**UPGRADE:** ${counts.UPGRADE}`] : []),
                          ...(counts.REDIRECT ? [`**REDIRECT:** ${counts.REDIRECT}`] : []),
                          ...(counts.KEEP ? [`**KEEP:** ${counts.KEEP}`] : []),
                          ``,
                          `## DELETE candidates`,
                          counts.DELETE ? top('DELETE') : '_(none)_',
                          ``,
                          `## UPGRADE candidates`,
                          counts.UPGRADE ? top('UPGRADE') : '_(none)_',
                          ``,
                          `## REDIRECT candidates`,
                          counts.REDIRECT ? top('REDIRECT') : '_(none)_',
                          ``,
                        ].join('\n');
                        navigator.clipboard.writeText(md);
                        alert('Notion-ready markdown copied. Paste into a Notion page or task description.');
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-800 bg-white hover:bg-gray-100 border border-gray-300 rounded-md"
                      title="Copy a Notion-ready markdown summary"
                    >
                      📝 Copy for Notion
                    </button>
                    <button
                      onClick={async () => {
                        if (!selectedCrawlId) return;
                        const audit_link = `${window.location.origin}/projects/${projectId}/website-audit/${selectedCrawlId}`;
                        setCreatingNotionTask(true);
                        setNotionTaskUrl(null);
                        try {
                          const resp = await api.post(
                            `/website-audit/crawl/${selectedCrawlId}/notion-task`,
                            { audit_link }
                          );
                          const url = resp.data?.page_url;
                          if (url) {
                            setNotionTaskUrl(url);
                          } else {
                            alert('Notion task created.');
                          }
                        } catch (err: any) {
                          alert('Failed to create Notion task: ' +
                            (err.response?.data?.detail || err.message));
                        } finally {
                          setCreatingNotionTask(false);
                        }
                      }}
                      disabled={creatingNotionTask}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-black hover:bg-gray-800 disabled:opacity-60 border border-black rounded-md"
                      title="Create a task in the Notion Tasks database"
                    >
                      {creatingNotionTask ? 'Creating…' : '✨ Create Notion Task'}
                    </button>
                  </div>
                )}
                {notionTaskUrl && (
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <span className="text-gray-600">Notion task:</span>
                    <a
                      href={notionTaskUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-700 hover:text-blue-900 underline break-all"
                    >
                      {notionTaskUrl}
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(notionTaskUrl);
                      }}
                      className="px-2 py-0.5 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded"
                      title="Copy Notion task URL"
                    >
                      Copy
                    </button>
                  </div>
                )}

                {/* Pagination */}
                {pageSize !== -1 && totalPages > 1 && (
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-3">
                    <p className="text-sm text-gray-600">
                      Showing {((safePage - 1) * effectivePageSize) + 1}-
                      {Math.min(safePage * effectivePageSize, totalRows)} of {totalRows.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={safePage === 1}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                      >
                        « First
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={safePage === 1}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                      >
                        ‹ Prev
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-700">
                        Page {safePage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                      >
                        Next ›
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={safePage === totalPages}
                        className="px-2 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                      >
                        Last »
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Send to Calendar Modal */}
      {showCalModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Send to Content Calendar</h2>
              <button
                onClick={() => setShowCalModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={calSubmitting}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {selectedUrls.size} {selectedUrls.size === 1 ? 'page' : 'pages'} selected
            </p>

            {calSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                {calSuccess}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={calActionType === 'rewrite'}
                    onChange={() => setCalActionType('rewrite')}
                  />
                  Rewrite existing article
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    checked={calActionType === 'new'}
                    onChange={() => setCalActionType('new')}
                  />
                  New idea
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
              <input
                type="date"
                value={calScheduledDate}
                onChange={e => setCalScheduledDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={calNotes}
                onChange={e => setCalNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Any context for the writer..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCalModal(false)}
                disabled={calSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSendToCalendar}
                disabled={calSubmitting || !calScheduledDate}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  calSubmitting || !calScheduledDate
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {calSubmitting ? 'Sending...' : 'Add to Calendar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Traffic Data Warning Modal */}
      {trafficWarning && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">{trafficWarning.title}</h2>
              </div>
              <button onClick={() => setTrafficWarning(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ul className="space-y-1 text-sm text-gray-700">
              {trafficWarning.lines.map((line, i) =>
                line === '' ? (
                  <li key={i} className="h-2" />
                ) : line.match(/^\d\./) ? (
                  <li key={i} className="ml-2">{line}</li>
                ) : line.startsWith('•') ? (
                  <li key={i} className="ml-2 text-gray-600">{line}</li>
                ) : (
                  <li key={i} className={i === 0 ? 'font-medium' : ''}>{line}</li>
                )
              )}
            </ul>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setTrafficWarning(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded-md hover:bg-gray-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

// ---------------------------------------------------------------------------
// SitemapNodeRow — recursive display of the sitemap discovery tree.
// Users can check any node to constrain the crawl to that sitemap.
// For sitemap index nodes, children are indented and independently selectable.
// ---------------------------------------------------------------------------
type SitemapNodeRowProps = {
  node: { url: string; type: string; url_count: number; children?: any[]; error?: string; is_likely_blog?: boolean };
  depth: number;
  selected: Set<string>;
  onToggle: (url: string) => void;
  disabled: boolean;
};

const SitemapNodeRow: React.FC<SitemapNodeRowProps> = ({ node, depth, selected, onToggle, disabled }) => {
  const isIndex = node.type === 'index' && node.children && node.children.length > 0;
  const label = (() => {
    try {
      const u = new URL(node.url);
      return u.pathname + (u.search || '');
    } catch {
      return node.url;
    }
  })();

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-1 hover:bg-white rounded"
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
      >
        <input
          type="checkbox"
          checked={selected.has(node.url)}
          onChange={() => onToggle(node.url)}
          disabled={disabled}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
        {/* Clickable URL — opens the sitemap XML in a new tab */}
        <a
          href={node.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs truncate flex-1 text-blue-600 hover:underline"
          title={`Open ${node.url} in a new tab`}
          onClick={(e) => e.stopPropagation()}
        >
          {label}
        </a>
        {node.is_likely_blog && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold whitespace-nowrap"
            title="Heuristic suggests this is a blog/article sitemap (filename or URL pattern). Review before tagging."
          >
            suggest: blog
          </span>
        )}
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            isIndex ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
          }`}
        >
          {isIndex ? 'index' : 'urlset'}
        </span>
        <span className="text-xs text-gray-600 tabular-nums min-w-[3rem] text-right">
          {node.url_count} URLs
        </span>
        {node.error && (
          <span className="text-xs text-red-600" title={node.error}>
            error
          </span>
        )}
      </div>
      {isIndex && node.children!.map((child, i) => (
        <SitemapNodeRow
          key={`${child.url}-${i}`}
          node={child}
          depth={depth + 1}
          selected={selected}
          onToggle={onToggle}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default WebsiteAuditPage;
